// CAPABILITY: Account settings UI — change email and change password.
import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { changeEmail, changePassword, mapAuthError } from "../../services/accountService";

function Banner({ tone, children }) {
  if (!children) return null;
  const cls =
    tone === "success"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : "border-rose-200 bg-rose-50 text-rose-700";
  return <div className={`rounded-lg border px-4 py-3 text-sm font-medium ${cls}`}>{children}</div>;
}

export default function AccountSettings() {
  const { currentUser } = useAuth();

  const [emailForm, setEmailForm] = useState({ newEmail: "", currentPassword: "" });
  const [emailState, setEmailState] = useState({ busy: false, error: "", success: "" });

  const [pwForm, setPwForm] = useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [pwState, setPwState] = useState({ busy: false, error: "", success: "" });

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailState({ busy: true, error: "", success: "" });
    try {
      await changeEmail({
        currentPassword: emailForm.currentPassword,
        newEmail: emailForm.newEmail,
      });
      setEmailState({
        busy: false,
        error: "",
        success: `Verification sent to ${emailForm.newEmail.trim()}. Click the link in that inbox to finish the change.`,
      });
      setEmailForm({ newEmail: "", currentPassword: "" });
    } catch (error) {
      setEmailState({ busy: false, error: mapAuthError(error), success: "" });
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirm) {
      setPwState({ busy: false, error: "New passwords do not match.", success: "" });
      return;
    }
    setPwState({ busy: true, error: "", success: "" });
    try {
      await changePassword({
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      });
      setPwState({ busy: false, error: "", success: "Password updated." });
      setPwForm({ currentPassword: "", newPassword: "", confirm: "" });
    } catch (error) {
      setPwState({ busy: false, error: mapAuthError(error), success: "" });
    }
  };

  return (
    <div className="space-y-6">
      {/* Email */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="text-base font-semibold text-slate-950">Email address</h3>
        <p className="mt-1 text-sm text-slate-500">
          Current: <span className="font-medium text-slate-700">{currentUser?.email || "—"}</span>
        </p>

        <form onSubmit={submitEmail} className="mt-4 space-y-4">
          <Banner tone="error">{emailState.error}</Banner>
          <Banner tone="success">{emailState.success}</Banner>

          <div className="field-group">
            <label htmlFor="new_email" className="field-label">New email</label>
            <input
              id="new_email"
              type="email"
              required
              value={emailForm.newEmail}
              onChange={(e) => setEmailForm((p) => ({ ...p, newEmail: e.target.value }))}
              className="field-input"
              placeholder="you@example.com"
            />
          </div>
          <div className="field-group">
            <label htmlFor="email_current_pw" className="field-label">Current password</label>
            <input
              id="email_current_pw"
              type="password"
              required
              value={emailForm.currentPassword}
              onChange={(e) => setEmailForm((p) => ({ ...p, currentPassword: e.target.value }))}
              className="field-input"
              placeholder="Confirm your password"
            />
          </div>
          <div className="flex justify-end">
            <button type="submit" className="primary-button" disabled={emailState.busy}>
              {emailState.busy ? "Sending…" : "Update email"}
            </button>
          </div>
        </form>
      </section>

      {/* Password */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="text-base font-semibold text-slate-950">Password</h3>
        <p className="mt-1 text-sm text-slate-500">Use at least 6 characters.</p>

        <form onSubmit={submitPassword} className="mt-4 space-y-4">
          <Banner tone="error">{pwState.error}</Banner>
          <Banner tone="success">{pwState.success}</Banner>

          <div className="field-group">
            <label htmlFor="pw_current" className="field-label">Current password</label>
            <input
              id="pw_current"
              type="password"
              required
              value={pwForm.currentPassword}
              onChange={(e) => setPwForm((p) => ({ ...p, currentPassword: e.target.value }))}
              className="field-input"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="field-group">
              <label htmlFor="pw_new" className="field-label">New password</label>
              <input
                id="pw_new"
                type="password"
                required
                minLength={6}
                value={pwForm.newPassword}
                onChange={(e) => setPwForm((p) => ({ ...p, newPassword: e.target.value }))}
                className="field-input"
              />
            </div>
            <div className="field-group">
              <label htmlFor="pw_confirm" className="field-label">Confirm new password</label>
              <input
                id="pw_confirm"
                type="password"
                required
                minLength={6}
                value={pwForm.confirm}
                onChange={(e) => setPwForm((p) => ({ ...p, confirm: e.target.value }))}
                className="field-input"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button type="submit" className="primary-button" disabled={pwState.busy}>
              {pwState.busy ? "Updating…" : "Update password"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
